<template>
  <div class="w-100">
    <AlertModal
      :open="deletModal.modal"
      message="Warning: Data will be deleted."
      sub-message="Are you sure you want to proceed?"
      :on-confirm="removeRow"
      :on-cancel="() => openDeleteModal(false)"
      button-name="Delete"
    />
    <div v-if="openInputs.length" class="w-100">
      <div
        v-for="(item, index) in model"
        :key="item.id"
        :class="`w-100 table-body  py-2 ${sub ? 'sub' : ''} ${
          !index || !(index % 2) ? 'white' : 'gray'
        } d-flex align-items-center justify-content-between`"
      >
        <p
          v-if="!openInputs[index]?.particular && item.particular"
          class="mb-0 body-title cursor-pointer"
          @click="openCloseInput('particular', true, item.id)"
        >
          {{ item.particular }}
        </p>
        <p
          v-if="!openInputs[index]?.particular && !item.particular"
          class="mb-0 body-title text-danger cursor-pointer"
          @click="openCloseInput('particular', true, item.id)"
        >
          Please Enter Name
        </p>
        <input
          :id="`input-particular-${item.id}`"
          v-model="model[index].particular"
          :hidden="!openInputs[index]?.particular"
          type="text"
          class="clickable-text left"
          @blur="openCloseInput('particular', false, item.id)"
          @keydown.enter.tab="(e) => handleTabEnter(e, item.id, 'particular')"
        />
        <div class="d-flex align-items-center">
          <p
            v-if="!openInputs[index]?.amount"
            class="body-title cursor-pointer mb-0 mr-3"
            @click="openCloseInput('amount', true, item.id)"
          >
            {{ NumberFormatter(item.amount) }}
          </p>
          <input
            :id="`input-amount-${item.id}`"
            v-model="model[index].amount"
            :hidden="!openInputs[index]?.amount"
            type="number"
            class="clickable-text w-100"
            @blur="openCloseInput('amount', false, item.id)"
            @keypress="validateAmount"
            @keydown.enter.tab="(e) => handleTabEnter(e, item.id, 'amount')"
          />
          <button
            v-if="!openInputs[index]?.amount"
            class="btn btn-outline-primary delete-btn"
            :disabled="status.value || $route.query.business === 'master'"
            @click="() => openDeleteModal(true, item.id)"
          >
            <img src="@/assets/svg/trash.svg" class="cursor-pointer" />
          </button>
        </div>
      </div>
    </div>
    <button
      class="add-btn btn-add mt-2 mb-2 btn btn-outline-primary"
      :disabled="status.value || $route.query.business === 'master'"
      @click="addNewRow"
    >
      {{ buttonName }}
    </button>
  </div>
</template>

<script setup>
import { onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { generate } from "randomized-string";
import { useStatusStore } from "../../store/statusStore";
import { NumberFormatter } from "../../helpers/numberFormatter";
import { AlertModal } from ".";
import { validateAmount } from "../../helpers/validation";

const props = defineProps({
  buttonName: {
    type: String,
    default: "Add",
  },
  businessQuery: {
    type: String,
    default: "current_business",
  },
});
const model = defineModel({ type: Array, default: [] });
const total = defineModel("total", { type: [Number, String], default: 0 });
const openInputs = ref([]);
const route = useRoute();
const statusStore = useStatusStore();
const { status } = storeToRefs(statusStore);

const deletModal = ref({
  modal: false,
  id: "",
});

const openDeleteModal = (modal, id) => {
  if (id) {
    deletModal.value.id = id;
  }
  deletModal.value.modal = modal;
};
const openCloseInput = async (key, value, id) => {
  if (status.value || route.query[props.businessQuery] === "master") {
    return;
  }
  if (window.location.pathname.includes("view")) {
    return;
  }
  const newOpenInputs = openInputs.value.map((item) => {
    if (item.id == id) {
      item[key] = value;
    }
    return item;
  });
  openInputs.value = await newOpenInputs;
  const inputField = await document.getElementById(`input-${key}-${id}`);
  if (inputField) {
    await inputField.focus();
  }
};

const addNewRow = async () => {
  if (status.value || route.query.business === "master") {
    return;
  }
  if (window.location.pathname.includes("view")) {
    return;
  }
  const id = generate({ charset: "number", length: 12 });
  model.value = await [
    ...model.value,
    {
      id,
      particular: "",
      amount: "",
    },
  ];
  openInputs.value = await [
    ...openInputs.value,
    { id, particular: false, amount: false },
  ];

  openCloseInput("particular", true, id);
};
const removeRow = async () => {
  if (status.value || route.query.business === "master") {
    return;
  }
  if (window.location.pathname.includes("view")) {
    return;
  }
  const newOpenInputs = openInputs.value.filter(
    (item) => item.id !== deletModal.value.id
  );
  const newModelData = model.value.filter(
    (item) => item.id !== deletModal.value.id
  );
  openInputs.value = await newOpenInputs;
  model.value = await newModelData;
};
watch(
  model,
  (newVal) => {
    const sum = newVal.reduce((a, b) => a + Number(b.amount || 0), 0);
    total.value = sum;
  },
  { deep: true }
);
const handleTabEnter = async (e, id, type) => {
  e.preventDefault();
  if (type === "amount") {
    const index = openInputs.value.findIndex((item) => item.id === id);
    const nextModel = openInputs.value[index + 1];
    if (nextModel) {
      await openCloseInput("particular", true, nextModel.id);
    } else {
      await addNewRow();
    }
  } else if (type === "particular") {
    await openCloseInput("particular", false, id);
    await openCloseInput("amount", true, id);
  }
};
onMounted(() => {
  if (model.value.length) {
    for (let i = 0; i < model.value.length; i++) {
      openInputs.value[i] = {
        id: model.value[i]?.id,
        particular: false,
        amount: false,
      };
    }
  }
});
</script>

<style scoped>
.table-body {
  min-height: 44px;
  padding-right: 100px;
  padding-left: 26px;
  border-bottom: 1px solid rgba(116, 113, 113, 0.3);
}
.table-body.sub {
  padding-left: 46px !important;
}
.body-title {
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 21px;
  letter-spacing: 0.055em;
  color: #382628;
}
.body-title.left {
  text-align: left;
}
.white {
  background: #fafbff;
}

.gray {
  background: #deeded;
}
.add-btn:disabled {
  opacity: 0.5;
}
.btn-add {
  width: unset;
  min-width: 150px !important;
}
.clickable-text.left {
  text-align: left !important;
}
.delete-btn {
  width: unset !important;
  margin-right: -52px !important;
}
@media screen and (max-width: 600px) {
  .table-body {
    padding-right: 54px !important;
  }
}
</style>
